import React from 'react'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'

// components
import {
  CSmartTable,
  CButton,
  CImage,
  CDropdown,
  CDropdownToggle,
  CDropdownMenu,
  CDropdownItem,
  CSmartPagination,
  CBadge,
  CFormInput
} from '@coreui/react-pro'

// icons
import ThreeDotsIconMenu from '../assets/icons/ThreeDotsOptionsIcon.png'

// styling
import { styleButtonVars3, styleButtonVars5 } from './custom-styles/buttonStyles'

// helpers
import { convertMoney } from '../helpers/format-number'

const AppTable = ({
  projectListPage,
  invoiceListPage,
  expenseListPage,
  clientListPage,
  payrollListPage,
  assetListPage,
  employeeListPage,
  supportingLinksListPage,
  userListPage,
  companyBankAccountListPage,
  companyPolicyListPage,
  policyTypeListPage,
  setShowConfirmDelete,
  setShowConfirmEdit,
  setShowConfirmDuplicate,
  selectable,
  handleOnRowClick,
  isClickable = false,
  tableColumns,
  tableContent,
  contentPerPage,
  activeContentPage,
  contentCount,
  setActiveContentPage,
  tableFilter,
  searchContentFilter,
  setSearchContentFilter,
  setContentPerPage,
  columnContentSorter,
  setColumnContentSorter,
  isShowModalEdit,
  isShowModalDuplicate = false,
  isLoading,
  isShowPagination = true,
  isShowDropdownMenu = true,
  isShowButtonCopy = false,
  handleCopyToClipboard,
  isShowButtonDelete = true,
  isShowTextProcessing = false,
  isShowColumnFilter = false,
  isShowButtonDuplicate = false
}) => {
  const navigate = useNavigate()

  return (
    <>
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3 gap-3">
        <div>
          <span className="me-2">Search:</span>
          <CFormInput
            placeholder="Search..."
            value={searchContentFilter}
            onChange={(e) => setSearchContentFilter(e?.target.value)}
            style={{ display: 'inline-block', width: '250px' }}
          />
        </div>
        {
          isShowTextProcessing && (
            <div>
              <CBadge color="warning">Processing...</CBadge>
            </div>
          )
        }
      </div>
      <CSmartTable
        columns={tableColumns}
        columnFilter={ isShowColumnFilter ? { external: true } : false}
        columnSorter={{ multiple: true, resetable: true, external: true }}
        sorterValue={columnContentSorter}
        items={tableContent}
        itemsPerPage={contentPerPage}
        itemsPerPageSelect={isShowPagination}
        onItemsPerPageChange={(itemsPerPage) => setContentPerPage(itemsPerPage)}
        tableProps={{
          className: 'app-table-content-list',
          hover: true,
          responsive: true,
          style: {
            cursor: 'pointer'
          }
        }}
        tableHeadProps={{
          className: 'app-table-header'
        }}
        pagination={isShowPagination}
        loading={isLoading}
        selectable={selectable}
        clickableRows={isClickable}
        onRowClick={(item, _, columnName) => handleOnRowClick(item, columnName)}
        onSorterChange={(value) => setColumnContentSorter(value)}
        scopedColumns={{
          name: (item) => (
            <td>
              {item?.name}
            </td>
          ),
          value: (item) => (
            <td>
              {`IDR ${convertMoney(String(item?.value))}`}
            </td>
          ),
          tax_value: (item) => (
            <td>
              {`IDR ${convertMoney(String(item?.tax_value))}`}
            </td>
          ),
          cash_inflow: (item) => (
            <td>
              {`IDR ${convertMoney(String(item?.cash_inflow))}`}
            </td>
          ),
          cash_outflow: (item) => (
            <td>
              {`IDR ${convertMoney(String(item?.cash_outflow))}`}
            </td>
          ),
          balance: (item) => (
            <td>
              {`IDR ${convertMoney(String(item?.balance))}`}
            </td>
          ),
          total_project: (item) => (
            <td>
              {`${item?.total_project > 0 ? `${item?.total_project} Projects` : 'Not available'}`}
            </td>
          ),
          date: (item) => (
            <td>
              {moment(item?.date).format('DD/MM/YYYY')}
            </td>
          ),
          register_date: (item) => (
            <td>
              {moment(item?.register_date).format('DD/MM/YYYY')}
            </td>
          ),
          start_date: (item) => (
            <td>
              {moment(item?.start_date).format('DD/MM/YYYY')}
            </td>
          ),
          end_date: (item) => (
            <td>
              {moment(item?.end_date).format('DD/MM/YYYY')}
            </td>
          ),
          due_date: (item) => (
            <td>
              {moment(item?.due_date).format('DD/MM/YYYY')}
            </td>
          ),
          expected_payment_date: (item) => (
            <td>
              {moment(item?.expected_payment_date).format('DD/MM/YYYY')}
            </td>
          ),
          tax_due_date: (item) => (
            <td>
              {moment(item?.tax_due_date).format('DD/MM/YYYY')}
            </td>
          ),
          is_paid: (item) => (
            <td>
              {item?.is_paid ? 'Yes' : 'No'}
            </td>
          ),
          bupot_required: (item) => (
            <td>
              {item?.bupot_required ? 'Yes' : 'No'}
            </td>
          ),
          faktur_required: (item) => (
            <td>
              {item?.faktur_required ? 'Yes' : 'No'}
            </td>
          ),
          payment_date: (item) => (
            <td>
              {item?.payment_date ? moment(item?.payment_date).format('DD/MM/YYYY') : '-'}
            </td>
          ),
          createdAt: (item) => (
            <td>
              {moment(item?.createdAt).format('DD/MM/YYYY')}
            </td>
          ),
          attachment_url: (item) => (
            <td>
              {
                item?.attachment_url ? (
                  <a href={item?.attachment_url} target='_blank' media='file'>
                    View
                  </a>
                )
                : (
                  <>
                    No attachment
                  </>
                )
              }
            </td>
          ),
          file_url: (item) => (
            <td>
              {
                item?.file_url ? (
                  <CButton
                    style={styleButtonVars5}
                    href={item?.file_url}
                    target='_blank'
                  >
                    View document
                  </CButton>
                )
                : (
                  <>
                    No attachment
                  </>
                )
              }
            </td>
          ),
          description: (item) => (
            <td>
              {item?.description?.length > 70 ? `${item?.description?.slice(0, 70)}...` : item?.description}
            </td>
          ),
          is_contract_base: (item) => (
            <td>
              {item?.is_contract_base ? 'Yes' : 'No'}
            </td>
          ),
          is_cash: (item) => (
            <td>
              {item?.is_cash ? 'Yes' : 'No'}
            </td>
          ),
          employee_no: (item) => (
            <td>
              {item?.employee_no ? item?.employee_no : '-'}
            </td>
          ),
          copy: (item) => (
            <td>
              {
                isShowButtonCopy && (
                  <CButton
                    style={styleButtonVars3}
                    onClick={() => {
                      handleCopyToClipboard(item?.letter_number)
                    }}
                  >
                    Copy
                  </CButton>
                )
              }
            </td>
          ),
          status_invoice: (item) => (
            <td>
              <CBadge color={item?.status_invoice === 'Draft' ? 'secondary' : 'success'}>
                {item?.status_invoice}
              </CBadge>
            </td>
          ),
          invoice_payment: (item) => (
            <td>
              <CBadge color={item?.invoice_payment === 'Not Paid' ? 'secondary' : 'success'}>
                {item?.invoice_payment}
              </CBadge>
            </td>
          ),
          action: (item, index) => (
            <td>
              <CDropdown>
                {
                  isShowDropdownMenu && (
                    <>
                      <CDropdownToggle
                        color="white"
                        caret={false}
                        style={{
                          border: "none",
                          boxShadow: "none",
                          background: "#FFF",
                          borderBottomRightRadius: ".3rem",
                          borderTopRightRadius: ".3rem",
                        }}
                        split
                      >
                        <CImage fluid src={ThreeDotsIconMenu} />
                      </CDropdownToggle>
                      <CDropdownMenu>
                        {
                          isShowButtonDuplicate && (
                            <CDropdownItem
                              onClick={() => {
                                if (isShowModalDuplicate) {
                                  setShowConfirmDuplicate({ show: true, type: invoiceListPage ? 'invoice' : expenseListPage ? 'expense' : payrollListPage ? 'payroll' : supportingLinksListPage ? 'supporting-link' : companyBankAccountListPage ? 'company-bank-account' : '', item, index })
                                } else {
                                  let urlDuplicate = projectListPage ? `/project/duplicate/${item.id}`
                                  : !isShowModalDuplicate && invoiceListPage ? `/invoice/duplicate/${item.id}`
                                    : !isShowModalDuplicate && expenseListPage ? `/expense/duplicate/${item.id}`
                                      : clientListPage ? `/client/duplicate/${item.id}`
                                          : employeeListPage ? `/employee/duplicate/${item.id}`
                                            : assetListPage ? `/asset/duplicate/${item.id}`
                                              : userListPage ? `/user/duplicate/${item.id}`
                                                : companyPolicyListPage ? `/company-policy/duplicate/${item.id}`
                                                  : policyTypeListPage ? `/policy-type/duplicate/${item.id}`
                                                    : null

                                  navigate(urlDuplicate)
                                }
                              }}
                            >
                              Duplicate {
                                projectListPage ? 'Project'
                                  : invoiceListPage ? 'Invoice'
                                    : expenseListPage ? 'Expense'
                                      : clientListPage ? 'Client'
                                        : payrollListPage ? 'Payroll'
                                          : employeeListPage ? 'Employee'
                                            : assetListPage ? 'Asset'
                                              : userListPage ? 'User'
                                                : companyBankAccountListPage ? 'Company Bank Account'
                                                  : companyPolicyListPage ? 'Company Policy'
                                                    : policyTypeListPage ? 'Policy Type'
                                                      : ''
                              }
                            </CDropdownItem>
                          )
                        }
                        <CDropdownItem
                          onClick={() => {
                            if (isShowModalEdit) {
                              setShowConfirmEdit({ show: true, type: invoiceListPage ? 'invoice' : expenseListPage ? 'expense' : payrollListPage ? 'payroll' : supportingLinksListPage ? 'supporting-link' : companyBankAccountListPage ? 'company-bank-account' : '', item, index })
                            } else {
                              let urlEdit = projectListPage ? `/project/edit/${item.id}`
                              : !isShowModalEdit && invoiceListPage ? `/invoice/edit/${item.id}`
                                : !isShowModalEdit && expenseListPage ? `/expense/edit/${item.id}`
                                  : clientListPage ? `/client/edit/${item.id}`
                                      : employeeListPage ? `/employee/edit/${item.id}`
                                        : assetListPage ? `/asset/edit/${item.id}`
                                          : userListPage ? `/user/edit/${item.id}`
                                            : companyPolicyListPage ? `/company-policy/edit/${item.id}`
                                              : policyTypeListPage ? `/policy-type/edit/${item.id}`
                                                : null

                              navigate(urlEdit)
                            }
                          }}
                        >
                          Edit {
                            projectListPage ? 'Project'
                              : invoiceListPage ? 'Invoice'
                                : expenseListPage ? 'Expense'
                                  : clientListPage ? 'Client'
                                    : payrollListPage ? 'Payroll'
                                      : employeeListPage ? 'Employee'
                                        : assetListPage ? 'Asset'
                                          : userListPage ? 'User'
                                            : companyBankAccountListPage ? 'Company Bank Account'
                                              : companyPolicyListPage ? 'Company Policy'
                                                : policyTypeListPage ? 'Policy Type'
                                                  : ''
                          }
                        </CDropdownItem>
                        {
                          isShowButtonDelete && (
                            <CDropdownItem
                              onClick={() => setShowConfirmDelete({ show: true, type: invoiceListPage ? 'invoices' : expenseListPage ? 'expenses' : payrollListPage ? 'payroll' : supportingLinksListPage ? 'supporting-link' : companyBankAccountListPage ? 'company-bank-account' : '' , item: item, index })}
                            >
                              Delete {
                                projectListPage ? 'Project'
                                  : invoiceListPage ? 'Invoice'
                                    : expenseListPage ? 'Expense'
                                      : clientListPage ? 'Client'
                                        : payrollListPage ? 'Payroll'
                                          : employeeListPage ? 'Employee'
                                            : assetListPage ? 'Asset'
                                              : userListPage ? 'User'
                                                : companyBankAccountListPage ? 'Company Bank Account'
                                                  : companyPolicyListPage ? 'Company Policy'
                                                    : policyTypeListPage ? 'Policy Type'
                                                      : ''
                              }
                            </CDropdownItem>
                          )
                        }
                      </CDropdownMenu>
                    </>
                  )
                }
              </CDropdown>
            </td>
          )
        }}
      />
      {
        isShowPagination && (
          <CSmartPagination
            align="center"
            activePage={activeContentPage}
            pages={Math.ceil(contentCount / contentPerPage) || 1}
            onActivePageChange={(activePage) => setActiveContentPage(activePage)}
            p
          />
        )
      }
    </div>
    </>
  )
}

export default AppTable
